import React from "react"
import { useTranslation } from "react-i18next"

import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { ProcessSteps } from "shared/ui/process-steps/ProcessSteps"

import { useProductConfiguratorStatusContext } from "../model/ProductConfiguratorStatusContext"
import { useProductConfiguratorViewModel } from "../model/useProductConfiguratorViewModel"
import "../styles/_product-configurator.scss"
import { ProductConfiguratorErrorHint } from "./ProductConfiguratorErrorHint"
import { ProductConfiguratorStatus } from "./ProductConfiguratorStatus"
import { ProductConfiguratorExternalDimensioningErrorHint } from "./external-dimensioning/ProductConfiguratorExternalDimensioningErrorHint"
import { ProductConfiguratorStepFactory } from "./steps/ProductConfiguratorStepFactory"
import { ProductConfiguratorSummaryCardConnector } from "./summary-card/ProductConfiguratorSummaryCardConnector"

export type ProductConfiguratorViewModel = ReturnType<
  typeof useProductConfiguratorViewModel
>

export const ProductConfiguratorView = React.memo(
  (viewModel: ProductConfiguratorViewModel) => {
    const { t } = useTranslation()
    const {
      className,
      hideTitle,
      productType,
      steps,
      activeStep,
      primaryButtonProps,
      secondaryButtonProps,
      hideSteps,
      hideFooter,
      onAdjustDimensioning,
      externalProductDimensioningModel,
      externalDimensioningBaseProductCardComponent,
      addonOptions,
      onAddonDelete
    } = viewModel
    const {
      isConfigurable,
      isConfigurationModelFaulty,
      isConfigurationServiceAvailable,
      isConfigurationWip
    } = useProductConfiguratorStatusContext()

    // Dimensioning owns the full width, and there is nothing to recap while the
    // configuration is still loading.
    const showSummaryCard =
      activeStep !== ProductConfiguratorStepType.DIMENSIONING &&
      activeStep !== ProductConfiguratorStepType.LOADING

    return (
      <div className={className}>
        {hideTitle ? null : (
          <h2 id="configuration" className="product-configurator__title">
            {t("Product.Configuration.Configuration")}
          </h2>
        )}
        <div className="product-configurator__header">
          {!hideSteps ? (
            <ProcessSteps steps={steps} activeValue={activeStep} />
          ) : null}
        </div>

        {isConfigurationWip ? (
          <div className="product-configurator__wip-banner">
            <ProductConfiguratorStatus
              productType={productType}
              isConfigurable={isConfigurable}
              isConfigurationModelFaulty={isConfigurationModelFaulty}
              isConfigurationServiceAvailable={isConfigurationServiceAvailable}
              isConfigurationWip={isConfigurationWip}
            />
          </div>
        ) : null}
        <ProductConfiguratorExternalDimensioningErrorHint
          onAdjustDimensioning={onAdjustDimensioning}
        />
        <ProductConfiguratorErrorHint />
        <div className="product-configurator__body">
          <div id="product-configurator" className="product-configurator__main">
            <ProductConfiguratorStepFactory
              activeStep={activeStep}
              externalProductDimensioningModel={
                externalProductDimensioningModel
              }
              externalDimensioningBaseProductCardComponent={
                externalDimensioningBaseProductCardComponent
              }
              addonOptions={addonOptions}
              onAddonDelete={onAddonDelete}
            />
          </div>

          {showSummaryCard ? (
            <ProductConfiguratorSummaryCardConnector
              showVariantKeysOnly={
                activeStep === ProductConfiguratorStepType.SUMMARY
              }
              className="product-configurator__summary-card"
            />
          ) : null}
        </div>

        {hideFooter ? null : (
          <div className="product-configurator__footer">
            <div className="product-configurator__footer-controls">
              <Button
                variant={ButtonVariant.SECONDARY}
                {...secondaryButtonProps}
                data-testid="product-configurator--previous-step-button"
                className="product-configurator__footer-controls-previous"
              />
              <Button
                variant={ButtonVariant.PRIMARY}
                {...primaryButtonProps}
                data-testid="product-configurator--next-step-button"
                className="product-configurator__footer-controls-next"
              />
            </div>
          </div>
        )}
      </div>
    )
  }
)
